/**
 * Vera Role Catalog — single source of truth for Entra app-role names.
 *
 * These values must exactly match the `Value` field on the app roles
 * defined in the Vera Platform Entra app registration. The BFF extracts
 * them from the `roles` token claim and exposes them via the auth context.
 *
 * See docs/vera-platform-identity-setup.md for the Azure Portal setup.
 */

export const VERA_MODULES = [
  'ops',
  'catalog',
  'sales',
  'rfp',
  'maple',
  'ideas',
  'marketing',
  'analytics',
  'finance',
  'implementation',
  'academy',
  'portfolio',
  'share',
  'rx',
] as const;
export type VeraModule = (typeof VERA_MODULES)[number];

/** Role prefix per module. Matches the `Value` field on the Entra app role. */
export const ROLE_PREFIX: Record<VeraModule, string> = {
  ops: 'Ops',
  catalog: 'Catalog',
  sales: 'Sales',
  rfp: 'Rfp',
  maple: 'Maple',
  ideas: 'Ideas',
  marketing: 'Marketing',
  analytics: 'Analytics',
  finance: 'Finance',
  implementation: 'Implementation',
  academy: 'Academy',
  portfolio: 'Portfolio',
  share: 'Share',
  rx: 'Rx',
};

/** Global super-admin role — unlocks every module. */
export const GLOBAL_ADMIN_ROLE = 'Admin';

/**
 * Shell-level role for the /admin/demos tab: publish, deactivate, and delete
 * the password-gated PUBLIC demo pages. Purely UI/BFF-level — no Dataverse
 * team sync (teamForRole returns null). The BFF's demoAdmin.ts hardcodes
 * this value (it can't import @shared) — keep the two in sync.
 */
export const DEMO_PAGES_ROLE = 'Shell.DemoPages';

/**
 * Shell-level role for the /admin/broadcasts tab: publish the message banner
 * that appears under the Vera header, to everyone or to named roles. Separate
 * from `Admin` so whoever owns internal comms can broadcast without holding
 * admin rights over all of Vera - the same reasoning that produced
 * Shell.DemoPages. Purely UI/BFF-level, no Dataverse team sync.
 */
export const BROADCAST_ROLE = 'Shell.Broadcast';

/**
 * Implementation Forms (impform) manager role: invite recipients, run the
 * review meeting, and release answers into Vera. Postgres-backed (impform
 * schema), so no Dataverse team sync (teamForRole returns null); the release
 * write path runs through the BFF. `Admin` is a superset.
 */
export const IMPFORM_MANAGER_ROLE = 'Impl.FormManager';

/**
 * Destructive-delete role for Vera Operations: delete all solutions on a
 * plan year, delete a plan year, and nuke GI data. Ops.Admin and global
 * Admin are supersets (see canOpsDelete). Deleting an ENTIRE group is
 * deliberately NOT covered; that action stays admin-only.
 */
export const OPS_DELETE_ROLE = 'Ops.Delete';

/**
 * Commission-economics role for Vera Finance: see commitment recipients and
 * rates, reveal the masked commission layer, and run commission periods.
 * Deliberately NOT implied by Finance.Admin or Finance.Manager - rep pay is
 * need-to-know (Sally's domain), so the grant is always explicit. Global
 * Admin remains a superset via the capability check. Postgres-backed
 * (finmon schema), so no Dataverse team sync (teamForRole returns null).
 */
export const FINANCE_COMMISSIONS_ROLE = 'Finance.Commissions';

/**
 * Admin-fee roles for the Rates & Fees plan-year surface (the admin fee
 * breakout, its tabs, rate sets, captive terms and budget census on a group).
 * Sasha, 9/04: the people who author a breakout (AE, imp manager, sales ops)
 * are not Finance-module people, and the person who approves one is not
 * necessarily either, so both acts get roles of their own instead of riding
 * Finance.* or Ops.Manager.
 *
 * Editor authors the WHOLE tab ("one exercise": breakout, rate sets, captive,
 * census) and submits tabs for approval; every Finance role already implies
 * it. Approver is the ONLY path to the approved and returned transitions,
 * deliberately not implied by Finance.Admin/Manager or any Ops role: the
 * separation of duty is the point. Both are Postgres-backed (finmon schema),
 * so no Dataverse team sync (teamForRole returns null).
 */
export const ADMIN_FEE_EDITOR_ROLE = 'AdminFee.Editor';
export const ADMIN_FEE_APPROVER_ROLE = 'AdminFee.Approver';

/**
 * Display groups for admin UIs (role pickers and checkbox lists). Purely
 * presentational: grouping never affects access checks or team sync.
 * Render order is alphabetical (see groupedVeraRoles), not the order here.
 */
export const VERA_ROLE_GROUPS = [
  'Platform',
  'Operations',
  'Catalog',
  'Sales',
  'Proposal',
  'Maple',
  'Ideas',
  'Marketing',
  'Finance',
  'Implementation',
  'Academy',
  'Portfolio',
  'Share',
  'Rx',
  'Analytics',
] as const;
export type VeraRoleGroup = (typeof VERA_ROLE_GROUPS)[number];

export interface VeraAppRole {
  value: string;
  displayName: string;
  description: string;
  /** Which VERA_ROLE_GROUPS bucket the role renders under in admin UIs. */
  group: VeraRoleGroup;
}

/**
 * All canonical role values that should be provisioned on the Vera Platform
 * app registration. When adding a new module, add its roles here and in the
 * Azure Portal (App Registration → App roles).
 */
export const VERA_APP_ROLES: readonly VeraAppRole[] = [
  { value: GLOBAL_ADMIN_ROLE, displayName: 'Vera Admin', description: 'Full admin access across all Vera modules', group: 'Platform' },
  { value: DEMO_PAGES_ROLE, displayName: 'Demo Pages Manager', description: 'Publish and manage public demo pages (Admin → Demo Pages). Content is effectively public — no PHI / confidential material.', group: 'Platform' },
  { value: BROADCAST_ROLE, displayName: 'Broadcast Manager', description: 'Publish the announcement banner under the Vera header, to everyone or to named roles (Admin → Broadcasts). Goes to a whole audience unreviewed — no PHI / group-specific detail.', group: 'Platform' },
  { value: 'Ops.Admin', displayName: 'Operations Admin', description: 'Admin features in Vera Operations', group: 'Operations' },
  { value: 'Ops.Manager', displayName: 'Operations Manager', description: 'Manage records on behalf of any Ops user; no admin/config access', group: 'Operations' },
  { value: 'Ops.User', displayName: 'Operations User', description: 'Access to Vera Operations', group: 'Operations' },
  { value: 'Ops.DataAdmin', displayName: 'Operations Data Admin', description: 'Account-link repair tools (CRM Matches): resolve duplicate-merge aftermath and fix a group’s Dynamics account. Bulk re-parents data — grant carefully.', group: 'Operations' },
  { value: 'Ops.SiteProvisioner', displayName: 'Site Provisioner', description: 'Provision and manage group SharePoint document sites (create sites, apply structure modules, sales archive move)', group: 'Operations' },
  { value: 'Ops.GuestAccess', displayName: 'Guest Access Manager', description: 'Send document-site invitations to external guests (brokers, group contacts) and revoke their access. The only role that can email clients from the Documents tab.', group: 'Operations' },
  { value: OPS_DELETE_ROLE, displayName: 'Operations Delete', description: 'Destructive plan-year deletes on a group: delete all solutions, delete a plan year, and nuke GI data. Deleting an entire group stays admin-only.', group: 'Operations' },
  { value: 'Catalog.Admin', displayName: 'Catalog Admin', description: 'Admin features in Vera Catalog', group: 'Catalog' },
  { value: 'Catalog.Manager', displayName: 'Catalog Manager', description: 'Manage records on behalf of any Catalog user; no admin/config access', group: 'Catalog' },
  { value: 'Catalog.User', displayName: 'Catalog User', description: 'Access to Vera Catalog', group: 'Catalog' },
  { value: 'Sales.Admin', displayName: 'Sales Admin', description: 'Admin features in Vera Sales', group: 'Sales' },
  { value: 'Sales.Manager', displayName: 'Sales Manager', description: 'Manage activities and pipeline on behalf of any Sales rep; no admin/config access', group: 'Sales' },
  { value: 'Sales.User', displayName: 'Sales User', description: 'Access to Vera Sales', group: 'Sales' },
  // Renamed RFP -> Proposal 2026-08-07 (Sasha): displayName, description, and
  // group are presentational. The `value` strings are NOT touched - every
  // grant is a vba_userroleassignment row keyed by this exact string, so
  // renaming them would revoke access for everyone who has it today.
  { value: 'Rfp.Admin', displayName: 'Proposal Admin', description: 'Admin features in Vera Proposal (answer-library curation, configuration)', group: 'Proposal' },
  { value: 'Rfp.Manager', displayName: 'Proposal Manager', description: 'Manage proposal projects and assignments on behalf of any Proposal user; no admin/config access', group: 'Proposal' },
  { value: 'Rfp.User', displayName: 'Proposal User', description: 'Access to Vera Proposal', group: 'Proposal' },
  { value: 'Maple.Admin', displayName: 'Maple Admin', description: 'Admin features in Maple (ingest-query editing, group/markup config)', group: 'Maple' },
  { value: 'Maple.Manager', displayName: 'Maple Manager', description: 'Manage claim batches (bill/collect/pay) on behalf of any Maple user; no admin/config access', group: 'Maple' },
  { value: 'Maple.User', displayName: 'Maple User', description: 'Access to Maple pharmacy claim billing/payment', group: 'Maple' },
  // Ideas is open to every authenticated employee — no Ideas.User role exists.
  // Ideas.Admin marks the tech team (status changes, merges); the BFF also
  // honors the seedable ideas.idea_admin Postgres table for the same gate.
  { value: 'Ideas.Admin', displayName: 'Ideas Admin', description: 'Tech-team controls in Vera Ideas (status, planned date, merge)', group: 'Ideas' },
  { value: 'Marketing.Admin', displayName: 'Marketing Admin', description: 'Admin features in Vera Marketing (Braze sync configuration)', group: 'Marketing' },
  { value: 'Marketing.User', displayName: 'Marketing User', description: 'Access to Vera Marketing (Braze sync status dashboard)', group: 'Marketing' },
  { value: 'Finance.Admin', displayName: 'Finance Admin', description: 'Admin features in Vera Finance: module configuration, per-group funding method and debit-convention setup', group: 'Finance' },
  { value: 'Finance.Manager', displayName: 'Finance Manager', description: 'Produce and publish financial deliverables (monthly monitoring, stop-loss renewals) on behalf of any Finance user; no admin/config access', group: 'Finance' },
  { value: 'Finance.User', displayName: 'Finance User', description: 'Access to Vera Finance: financial monitoring, stop-loss renewals, reconciliation, and the fee registry. PHI-bearing surfaces (spec claimants, ICD-10) render behind a masked-by-default toggle.', group: 'Finance' },
  { value: FINANCE_COMMISSIONS_ROLE, displayName: 'Finance Commissions', description: 'Commission economics in the fee registry: commitment recipients and rates, the masked commission layer, and commission runs. Not implied by Finance Admin/Manager - always an explicit grant; every reveal and run is audited.', group: 'Finance' },
  { value: ADMIN_FEE_EDITOR_ROLE, displayName: 'Admin Fee Editor', description: 'Author the Rates & Fees surface on any group (admin fee breakout, tabs, rate sets, captive terms, budget census) and submit tabs for approval, without the rest of Vera Finance. Every Finance role already includes this ability. Broker commission stays masked without Finance Commissions.', group: 'Finance' },
  { value: ADMIN_FEE_APPROVER_ROLE, displayName: 'Admin Fee Approver', description: 'Approve or return submitted admin-fee breakout tabs from the Approvals queue. The ONLY path to the Approved status: deliberately not implied by Finance Admin/Manager or any Ops role, so authoring and approving stay separate duties.', group: 'Finance' },
  { value: IMPFORM_MANAGER_ROLE, displayName: 'Implementation Form Manager', description: 'Manage implementation forms: invite external recipients, run review, and release answers into Vera', group: 'Implementation' },
  // Academy is open to every authenticated employee (Ideas precedent): reading
  // Learn and Know needs no role. Academy.User exists for per-item restricted
  // content (content_item.visibility='role') and future audience predicates,
  // not as an access gate. Postgres-backed (academy schema), no Dataverse
  // team sync (teamForRole returns null).
  { value: 'Academy.User', displayName: 'Academy User', description: 'Reserved for restricted Academy content and audience targeting. NOT required to read Learn or Know: the module is open to every authenticated Veraciter.', group: 'Academy' },
  { value: 'Academy.Author', displayName: 'Academy Author', description: 'Create, edit, and publish Academy content: Know articles, lessons, collections, and the glossary. Writing content is safe; assigning training is not, so assignment stays on Academy.Admin.', group: 'Academy' },
  { value: 'Academy.Manager', displayName: 'Academy Manager', description: 'See training completion for your reporting chain (My Team). No authoring or configuration access.', group: 'Academy' },
  { value: 'Academy.Admin', displayName: 'Academy Admin', description: 'Full Academy administration: authoring, assignment rules with due dates, waive/revoke, and org-wide completion and score reporting.', group: 'Academy' },
  // Initiative Portfolio (2026-08-08, gated same day at Sasha's request:
  // originally open to all, now role-gated). Portfolio.User is the read gate
  // for the whole module; the accountable person / delivery lead on an
  // initiative can edit it and submit snapshots as a data-level check on top
  // of that, not a separate role. Portfolio.Sensitive additionally reveals
  // initiatives marked sensitive (comp studies, M&A, re-orgs); an
  // initiative's own owner/lead and portfolio admins always see it.
  // Postgres-backed (portfolio schema), no Dataverse team sync.
  { value: 'Portfolio.User', displayName: 'Portfolio User', description: 'See the Initiative Portfolio: the register, dashboards, and initiative detail. Owners and delivery leads also need this role to reach their update flow.', group: 'Portfolio' },
  { value: 'Portfolio.Sensitive', displayName: 'Portfolio Sensitive Access', description: 'See initiatives marked sensitive (in addition to Portfolio User). Not implied by Portfolio Admin being separate: admins DO see sensitive initiatives; this role exists for executives who are not admins.', group: 'Portfolio' },
  { value: 'Portfolio.Updater', displayName: 'Portfolio Updater', description: 'Submit monthly updates and manage blockers on ANY initiative: the delegation role for people the accountable person hands the update ritual to. My Updates becomes their full worklist. No edit rights over titles, ownership, or admin fields, and sensitive initiatives still need Portfolio Sensitive Access.', group: 'Portfolio' },
  { value: 'Portfolio.Admin', displayName: 'Portfolio Admin', description: 'Administer the Initiative Portfolio: create and archive initiatives, reassign ownership, mark initiatives sensitive, curate business units and strategic pillars, and backfill snapshot history. Sees sensitive initiatives.', group: 'Portfolio' },
  // VELT, the weekly executive leadership review (2026-08-12, Sasha).
  // Deliberately NOT implied by Portfolio.Admin: an admin curates the
  // register, which is a different job from sitting in the leadership call.
  // Orthogonal to Portfolio.Sensitive too: a VELT member without it simply
  // does not see sensitive initiatives on the agenda.
  { value: 'Portfolio.Leadership', displayName: 'Portfolio Leadership (VELT)', description: 'Use the VELT leadership review: build weekly meeting agendas, flag initiatives onto the VELT watch list, record the notes taken on the call, and own leadership action items. Sensitive initiatives still require Portfolio Sensitive Access.', group: 'Portfolio' },
  // Vera Share (2026-08-13, Sasha): internal HTML publishing that MAY carry
  // PHI, so access is two gates and both are required. Share.User is the
  // module gate (it limits who can see the module exists at all); being named
  // on a file is the file gate. Neither works alone. This is the deliberate
  // opposite of the public demo pages, whose content is effectively public.
  // Postgres-backed (share schema), no Dataverse team sync.
  { value: 'Share.User', displayName: 'Share User', description: 'Open files published to you in Vera Share. You see ONLY the files someone named you on: this role by itself shows an empty list. Required for every recipient, including recipients of files that contain PHI.', group: 'Share' },
  { value: 'Share.Publisher', displayName: 'Share Publisher', description: 'Publish HTML files to Vera Share and choose who receives them. This is the privileged grant in the module: a publisher decides who sees the contents, which may include PHI. Publishers manage only the files they published.', group: 'Share' },
  { value: 'Share.Admin', displayName: 'Share Admin', description: 'Administer Vera Share: see and manage every published file and recipient list regardless of who published it, extend or deactivate expiring files, and delete. Implies publishing.', group: 'Share' },
  // Vera Rx (2026-08-13, Sasha): the Rx savings analytics module. The evidence
  // layer is aggregate book-of-business data, but a prospect analysis carries
  // that prospect's claims, so the module is role-gated and not open to all.
  // Postgres-backed (rx schema), no Dataverse team sync.
  { value: 'Rx.User', displayName: 'Rx User', description: 'Use Vera Rx: book-of-business savings by drug and period, and read prospect savings runs. Read-only; cannot refresh the evidence, edit savings expectations, or upload a prospect claim file.', group: 'Rx' },
  // Refresh alone, without the rest of Rx.Admin (Sasha, 2026-08-17). Pulling the
  // evidence spends Fabric capacity and restates every percentage the module
  // shows, but it changes no judgement: the rules, expectations and lists all
  // stay where they were. So it is worth being able to grant to whoever watches
  // the data without also handing over what the model believes.
  { value: 'Rx.Refresh', displayName: 'Rx Refresh', description: 'Pull the book-of-business evidence from Fabric on demand, without the rest of Rx Admin. The refresh re-reads the gold lakehouse and rewrites the recent evidence months, so every savings figure in the module restates; it changes no rule, expectation or reference list. Intended for whoever keeps the data current. Rx Admin already includes this.', group: 'Rx' },
  { value: 'Rx.Admin', displayName: 'Rx Admin', description: 'Administer Vera Rx: refresh the book-of-business evidence from Fabric, edit the PIP and specialty savings expectations the model uses, maintain the pharmacy-to-channel mapping, and upload prospect claim files (which contain PHI) to run a savings analysis.', group: 'Rx' },
  // Vera Analytics (2026-09-03 as Vera Clinical; renamed 9/04 PRE-DEPLOY, so
  // the role VALUE was renamed too - the one time that is safe. Any
  // Clinical.User grant made before the rename is inert and needs a re-grant
  // of Analytics.User). Claims + eligibility exploration over the vClinical
  // Fabric medallion, plus the source-ID workbench. The dashboards aggregate,
  // but the claim-detail drill is member-level PHI, so the module is
  // role-gated and not open to all. Reads Fabric through the BFF only;
  // workbench writes are Dataverse partner-ref writes on the user's own
  // delegated token, so they additionally need the user's existing
  // Catalog/Ops Dataverse access. No Dataverse team sync (teamForRole null).
  { value: 'Analytics.User', displayName: 'Analytics User', description: 'Use Vera Analytics: claims and eligibility dashboards over the data platform, claim detail drill-down (member-level PHI), and the source-ID reconciliation workbench. Assigning IDs to groups also requires existing partner-table access (a Catalog or Ops role).', group: 'Analytics' },
] as const;

/**
 * VERA_APP_ROLES bucketed by group. Groups, and the roles within each, sort
 * alphabetically (standing rule 2026-09-04: lists are alphabetized unless a
 * specific order was designed). Declaration order above carries no meaning.
 * Groups with no roles are dropped so admin UIs can map over the result
 * without empty-header checks.
 */
export function groupedVeraRoles(): { group: VeraRoleGroup; roles: VeraAppRole[] }[] {
  return [...VERA_ROLE_GROUPS]
    .sort((a, b) => a.localeCompare(b))
    .map((group) => ({
      group,
      roles: VERA_APP_ROLES
        .filter((r) => r.group === group)
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    }))
    .filter((g) => g.roles.length > 0);
}

// ---------------------------------------------------------------------------
// Dataverse team sync
// ---------------------------------------------------------------------------

/**
 * Each Vera role maps to a plain Dataverse team. Granting a role
 * adds the user to that team so they inherit the team's security
 * role (actual data-layer access). Revoking removes them — unless
 * another role the user still holds maps to the same team.
 *
 * `<Prefix>.User` and `<Prefix>.Admin` intentionally map to the same
 * team: Dataverse-level data access is the same for both (the .Admin
 * distinction gates UI features, not data).
 *
 * The provision-vera-teams script creates these teams and attaches
 * the matching Dataverse security roles (Vera Admin, Vera Ops User,
 * Vera Catalog User, Vera Sales User — created separately via the
 * Power Apps maker portal).
 */
export const VERA_TEAM_NAMES = {
  ADMIN: 'Vera Admins Team',
  OPS: 'Vera Ops Team',
  CATALOG: 'Vera Catalog Team',
  SALES: 'Vera Sales Team',
  MARKETING: 'Vera Marketing Team',
} as const;

export type VeraTeamName = (typeof VERA_TEAM_NAMES)[keyof typeof VERA_TEAM_NAMES];

/**
 * Which Dataverse security role attaches to each team. The roles
 * already exist in the tenant (created during the legacy setup —
 * see docs/vera-entra-app-roles-setup.md).
 */
export const TEAM_TO_DATAVERSE_ROLE: Record<VeraTeamName, string> = {
  [VERA_TEAM_NAMES.ADMIN]: 'Vera Admin',
  [VERA_TEAM_NAMES.OPS]: 'Vera Ops User',
  [VERA_TEAM_NAMES.CATALOG]: 'Vera Catalog User',
  [VERA_TEAM_NAMES.SALES]: 'Vera Sales User',
  [VERA_TEAM_NAMES.MARKETING]: 'Vera Marketing User',
};

/**
 * Role → team mapping. A role returns null if it doesn't need team sync
 * (reserved for future roles that are purely UI-level).
 */
export function teamForRole(roleValue: string): VeraTeamName | null {
  if (roleValue === GLOBAL_ADMIN_ROLE) return VERA_TEAM_NAMES.ADMIN;
  if (roleValue.startsWith('Ops.')) return VERA_TEAM_NAMES.OPS;
  if (roleValue.startsWith('Catalog.')) return VERA_TEAM_NAMES.CATALOG;
  if (roleValue.startsWith('Sales.')) return VERA_TEAM_NAMES.SALES;
  // Marketing reads vba_brazesynclog via the delegated dataverseClient, so its
  // roles sync to the Vera Marketing Team (read-only on that one table).
  if (roleValue.startsWith('Marketing.')) return VERA_TEAM_NAMES.MARKETING;
  // Vera Proposal (the module formerly shown as RFP; role values are still
  // Rfp.*) is Postgres-backed; its Dataverse reads (Account/Opportunity
  // cross-links) go through the BFF service identity, not per-user teams, so
  // Rfp.* roles need no Dataverse team sync — they fall through to null.
  // Maple is likewise Postgres-backed (maplebridge schema) — no team sync.
  // Ideas too (ideas schema): open to all employees, no Dataverse team.
  // Impl.FormManager (impform schema) also needs no team: the release
  // write path into Dataverse runs through the BFF, not per-user teams.
  // Academy (academy schema) is Postgres-backed too: no team sync.
  // Portfolio (portfolio schema) likewise: open-to-all reads via the BFF,
  // no Dataverse anywhere in the module.
  // Vera Share (share schema) too: the module stores its own documents and
  // recipient lists and never touches Dataverse.
  // Vera Rx (rx schema) reads Fabric and its own Postgres tables only.
  // Vera Analytics reads Fabric through the BFF; its workbench writes are
  // Dataverse partner-ref writes on the user's own token, covered by the
  // user's existing Catalog/Ops team membership, not an Analytics team.
  // AdminFee.* (finmon schema) likewise: the breakout lives in Postgres and
  // its Dataverse reads (the stamp) run on the acting user's delegated token.
  return null;
}

/**
 * All role values (from the catalog) that map to a given team. Used to
 * decide whether to remove a user from a team when a role is revoked —
 * only remove if the user holds no other role for the same team.
 */
export function rolesForTeam(team: VeraTeamName): string[] {
  return VERA_APP_ROLES.map((r) => r.value).filter((v) => teamForRole(v) === team);
}

const ALL_PREFIXES = Object.values(ROLE_PREFIX);

export function hasGlobalAdmin(roles: readonly string[]): boolean {
  return roles.includes(GLOBAL_ADMIN_ROLE);
}

// Named capability helpers (canManageDemoPages, canManageImpForms,
// canManageCrmMatches, canOpsDelete) moved to ./capabilities.ts, backed by
// the capability registry. Import them from '@shared/auth' as before.

export function isModuleAdmin(roles: readonly string[], rolePrefix: string): boolean {
  return hasGlobalAdmin(roles) || roles.includes(`${rolePrefix}.Admin`);
}

/**
 * True if the user can act on behalf of other users in the module — manager-or-admin tier.
 *
 * Use for features that let a privileged user enter, edit, or delete records
 * owned by other users (e.g., a sales manager logging activities on behalf of
 * a rep). Admin always implies manager. Manager does NOT imply admin —
 * configuration screens should still gate on `isModuleAdmin`.
 */
export function isModuleManager(roles: readonly string[], rolePrefix: string): boolean {
  return (
    hasGlobalAdmin(roles) ||
    roles.includes(`${rolePrefix}.Admin`) ||
    roles.includes(`${rolePrefix}.Manager`)
  );
}

/**
 * Global Admin or ANY module's .Admin role. For cross-module admin
 * affordances that aren't tied to one module (impersonation, geocoding
 * tools, the no-prefix useIsAdmin()).
 */
export function isAnyModuleAdmin(roles: readonly string[]): boolean {
  return hasGlobalAdmin(roles) || roles.some((r) => r.endsWith('.Admin'));
}

/** Global Admin or ANY module's .Admin or .Manager role. */
export function isAnyModuleManager(roles: readonly string[]): boolean {
  return isAnyModuleAdmin(roles) || roles.some((r) => r.endsWith('.Manager'));
}

export function hasModuleAccess(roles: readonly string[], rolePrefix: string): boolean {
  return (
    hasGlobalAdmin(roles) ||
    roles.some((r) => r.startsWith(`${rolePrefix}.`))
  );
}

/**
 * True if the user's token carries any Vera-shaped role claim.
 *
 * Used as a "Phase 0 fallback" gate: during the period before the Vera
 * Platform app registration is live, users authenticate through a legacy
 * per-app registration and have zero canonical Vera roles. The shell
 * treats that case as full access so developers aren't locked out.
 * Once Phase 1 is rolled out, every real user will have at least one
 * Vera role and the fallback is no longer reached.
 */
export function hasAnyVeraRole(roles: readonly string[]): boolean {
  if (hasGlobalAdmin(roles)) return true;
  if (roles.includes(DEMO_PAGES_ROLE)) return true;
  if (roles.includes(IMPFORM_MANAGER_ROLE)) return true;
  // AdminFee.* are standalone (no module prefix): an author or approver whose
  // ONLY grant is one of these still counts as having a Vera role.
  if (roles.includes(ADMIN_FEE_EDITOR_ROLE) || roles.includes(ADMIN_FEE_APPROVER_ROLE)) return true;
  return roles.some((r) => ALL_PREFIXES.some((p) => r.startsWith(`${p}.`)));
}
